import {AnalyticsConfig} from "./AnalyticsConfig";

export class Analytics {
	protected static readonly KEY_VID = '_vp_vid';
	protected static readonly KEY_SID = '_vp_sid';
	protected static readonly KEY_SID_TS = '_vp_sid_ts';
	protected static readonly KEY_IREF = '_vp_iref';
	protected static readonly KEY_UID = '_vp_uid';
	protected static readonly KEY_PLC = '_vp_plc';     // page load count
	protected static readonly KEY_PLC_TS = '_vp_plct'; // timestamp of last count reset
	protected static readonly KEY_LP = '_vp_lp';       // landing page
	protected static readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min inactivity timeout
	protected static readonly RESET_TRACKING_COUNT_H = 24; // reset page load counter every 24 hours
	protected static readonly COLLECT_PATH = '/api/v1/a/col';
	protected static readonly EXPOSE_API = false;
	protected static readonly ENCRYPTION_KEY = 'd847af21198140db3ecf22bd78055dc4fd1e901dc60953700751fb16107d4b22';
	protected static readonly ENCRYPTION_PREFIX = 'E1:';

	protected config: AnalyticsConfig;
	protected endpoint: string;
	protected token: string;
	protected visitorId: string;
	protected sessionId: string;
	protected isNewVisitor: boolean;
	protected isNewSession: boolean;
	protected initialReferrer: string;
	protected userId: string;
	protected landingPage: string;
	protected lastUrl: string;
	protected encryptKey: CryptoKey | null = null;

	// Engaged time tracking (GA4-style visible-only time)
	protected engageVisibleSince: number | null = null;
	protected engageTotalMs: number = 0;

	constructor(config: AnalyticsConfig) {
		this.config = config;
		if (this.config.disableAnalytics) {
			return;
		}
		this.endpoint = this.config.gatewayOrigin + Analytics.COLLECT_PATH;
		this.token = this.config.publicToken || '';
		this.lastUrl = location.pathname + location.search;

		// Visitor ID (localStorage — persists across sessions/tabs)
		const existingVid = localStorage.getItem(Analytics.KEY_VID);
		if (existingVid) {
			this.visitorId = existingVid;
			this.isNewVisitor = false;
		}
		else {
			this.visitorId = this.generateUUID();
			this.isNewVisitor = true;
			localStorage.setItem(Analytics.KEY_VID, this.visitorId);
		}

		// Session ID (sessionStorage — scoped to tab, clears on close)
		const existingSid = sessionStorage.getItem(Analytics.KEY_SID);
		const lastActivity = parseInt(sessionStorage.getItem(Analytics.KEY_SID_TS) || '0', 10);
		const sessionExpired = lastActivity > 0 && (Date.now() - lastActivity) > Analytics.SESSION_TIMEOUT_MS;
		if (existingSid && !sessionExpired) {
			this.sessionId = existingSid;
			this.isNewSession = false;
		}
		else {
			this.sessionId = this.generateUUID();
			this.isNewSession = true;
		}
		this.touchSession();

		// Initial referrer: store on new session, read from sessionStorage on existing
		if (this.isNewSession) {
			this.initialReferrer = document.referrer || '';
			sessionStorage.setItem(Analytics.KEY_IREF, this.initialReferrer);
			this.landingPage = location.pathname + location.search;
			sessionStorage.setItem(Analytics.KEY_LP, this.landingPage);
		}
		else {
			this.initialReferrer = sessionStorage.getItem(Analytics.KEY_IREF) || '';
			this.landingPage = sessionStorage.getItem(Analytics.KEY_LP) || '';
		}

		// User ID (localStorage — persists across sessions)
		this.userId = localStorage.getItem(Analytics.KEY_UID) || '';

		// Start engagement tracking
		this.engageStart();

		// Visibility change: stop/start engagement, flush on hidden
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') {
				this.engageStop();
				this.flushEngagement();
			}
			else {
				this.engageStart();
			}
		});

		// pagehide — more reliable than visibilitychange on mobile Safari
		window.addEventListener('pagehide', () => {
			this.flushEngagement();
		});

		// blur — catches alt-tab scenarios
		window.addEventListener('blur', () => {
			setTimeout(() => this.flushEngagement(), 0);
		});

		// SPA route tracking: hook pushState + replaceState
		this.hookHistoryMethod('pushState');
		this.hookHistoryMethod('replaceState');
		window.addEventListener('popstate', () => {
			this.dispatchRouteChange();
		});
		window.addEventListener('vp:routechange', () => {
			this.onRouteChange();
		});

		// Import AES encryption key
		this.initEncryptionKey();

		// Track pageview after load for accurate page load time
		if (document.readyState === 'complete') {
			this.trackPageView();
		}
		else {
			window.addEventListener('load', () => this.trackPageView());
		}

		if (Analytics.EXPOSE_API) {
			(window as any).vpayAnalytics = this;
		}
	}

	public trackPageView(): void {
		const utm = this.getUTMParams();
		const payload: Record<string, unknown> = {
			vid: this.visitorId,
			sid: this.sessionId,
			ns: this.isNewSession,
			nv: this.isNewVisitor,
			hn: location.hostname,
			p: location.pathname + location.search,
			t: document.title,
			r: document.referrer,
			ir: this.initialReferrer,
			lp: this.landingPage,
			us: utm['utm_source'] || '',
			um: utm['utm_medium'] || '',
			uc: utm['utm_campaign'] || '',
			ut: utm['utm_term'] || '',
			ux: utm['utm_content'] || '',
			sw: screen.width,
			sh: screen.height,
			vw: window.innerWidth,
			vh: window.innerHeight,
			lang: navigator.language || '',
			tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
			plt: this.getPageLoadTime(),
			et: 'pageview',
		};
		if (this.token) {
			payload['token'] = this.token;
		}
		if (this.userId) {
			payload['uid'] = this.userId;
		}
		this.send(payload);

		// After first pageview, clear new session/visitor flags
		this.isNewSession = false;
		this.isNewVisitor = false;
	}

	public trackEvent(name: string, data?: Record<string, unknown>): void {
		const payload: Record<string, unknown> = {
			vid: this.visitorId,
			sid: this.sessionId,
			ns: false,
			nv: false,
			hn: location.hostname,
			p: location.pathname + location.search,
			t: document.title,
			et: 'event',
			en: name,
		};
		if (data) {
			payload['ed'] = JSON.stringify(data);
		}
		if (this.token) {
			payload['token'] = this.token;
		}
		if (this.userId) {
			payload['uid'] = this.userId;
		}
		this.send(payload);
	}

	public identify(userId: string): void {
		this.userId = userId;
		localStorage.setItem(Analytics.KEY_UID, userId);
		// Send an identify event so the backend links visitor_id to this user
		const payload: Record<string, unknown> = {
			vid: this.visitorId,
			sid: this.sessionId,
			hn: location.hostname,
			p: location.pathname + location.search,
			et: 'event',
			en: 'identify',
			uid: userId,
		};
		if (this.token) {
			payload['token'] = this.token;
		}
		this.send(payload);
	}

	// ################################################################
	// ###################### PRIVATE FUNCTIONS #######################

	protected engageStart(): void {
		if (this.engageVisibleSince === null) {
			this.engageVisibleSince = Date.now();
		}
	}

	protected engageStop(): void {
		if (this.engageVisibleSince !== null) {
			this.engageTotalMs += Date.now() - this.engageVisibleSince;
			this.engageVisibleSince = null;
		}
	}

	protected engageCurrent(): number {
		let total = this.engageTotalMs;
		if (this.engageVisibleSince !== null) {
			total += Date.now() - this.engageVisibleSince;
		}
		return total;
	}

	protected flushEngagement(): void {
		const etm = this.engageCurrent();
		if (etm < 100) return; // skip sub-100ms flushes
		const payload: Record<string, unknown> = {
			vid: this.visitorId,
			sid: this.sessionId,
			hn: location.hostname,
			p: location.pathname + location.search,
			et: 'pageview',
			etm: etm,
		};
		if (this.token) {
			payload['token'] = this.token;
		}
		if (this.userId) {
			payload['uid'] = this.userId;
		}
		this.send(payload);
	}

	// Replaced by flushEngagement() which tracks visible-only engaged time (GA4-style) instead of wall clock time
	// protected sendTimeOnPage(): void {
	//     const seconds = Math.round((Date.now() - this.pageEnteredAt) / 1000);
	//     if (seconds < 1) return;
	//     const payload: Record<string, unknown> = {
	//         vid: this.visitorId,
	//         sid: this.sessionId,
	//         hn: location.hostname,
	//         p: location.pathname + location.search,
	//         et: 'pageview',
	//         top: seconds,
	//     };
	//     if (this.token) {
	//         payload['token'] = this.token;
	//     }
	//     this.send(payload);
	// }

	protected hookHistoryMethod(method: 'pushState' | 'replaceState'): void {
		const original = history[method];
		history[method] = (...args: Parameters<typeof history.pushState>) => {
			original.apply(history, args);
			this.dispatchRouteChange();
		};
	}

	protected dispatchRouteChange(): void {
		window.dispatchEvent(new Event('vp:routechange'));
	}

	protected onRouteChange(): void {
		const newUrl = location.pathname + location.search;
		if (newUrl === this.lastUrl) return;
		// Flush engagement for the previous page
		this.flushEngagement();
		// Reset engagement for the new page
		this.engageTotalMs = 0;
		this.engageVisibleSince = null;
		this.engageStart();
		// Record previous path and update lastUrl
		const previousUrl = this.lastUrl;
		this.lastUrl = newUrl;
		// Track the new page with previous path
		const utm = this.getUTMParams();
		const payload: Record<string, unknown> = {
			vid: this.visitorId,
			sid: this.sessionId,
			ns: false,
			nv: false,
			hn: location.hostname,
			p: newUrl,
			pp: previousUrl,
			t: document.title,
			r: document.referrer,
			ir: this.initialReferrer,
			lp: this.landingPage,
			us: utm['utm_source'] || '',
			um: utm['utm_medium'] || '',
			uc: utm['utm_campaign'] || '',
			ut: utm['utm_term'] || '',
			ux: utm['utm_content'] || '',
			sw: screen.width,
			sh: screen.height,
			vw: window.innerWidth,
			vh: window.innerHeight,
			lang: navigator.language || '',
			tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
			plt: 0,
			et: 'pageview',
		};
		if (this.token) {
			payload['token'] = this.token;
		}
		if (this.userId) {
			payload['uid'] = this.userId;
		}
		this.send(payload);
	}

	protected send(data: Record<string, unknown>): void {
		// Check page load tracking limit (daily reset)
		const limit = this.config.trackPageLoads;
		if (limit && limit > 0) {
			let count = parseInt(localStorage.getItem(Analytics.KEY_PLC) || '0', 10);
			const resetTs = parseInt(localStorage.getItem(Analytics.KEY_PLC_TS) || '0', 10);
			const resetMs = Analytics.RESET_TRACKING_COUNT_H * 3600 * 1000;
			if (resetTs > 0 && (Date.now() - resetTs) > resetMs) {
				count = 0;
				localStorage.setItem(Analytics.KEY_PLC, '0');
				localStorage.setItem(Analytics.KEY_PLC_TS, Date.now().toString());
			}
			if (count >= limit) {
				return;
			}
			if (data['et'] === 'pageview' && !data['etm']) {
				localStorage.setItem(Analytics.KEY_PLC, (count + 1).toString());
				if (!resetTs) {
					localStorage.setItem(Analytics.KEY_PLC_TS, Date.now().toString());
				}
			}
		}

		const json = JSON.stringify(data);
		if (this.encryptKey) {
			this.encryptAndSend(json);
		}
		else {
			this.sendRaw(btoa(json));
		}
		this.touchSession();
	}

	protected sendRaw(body: string): void {
		// Use text/plain content-type to avoid CORS preflight
		if (typeof navigator.sendBeacon === 'function') {
			const blob = new Blob([body], {type: 'text/plain'});
			navigator.sendBeacon(this.endpoint, blob);
		}
		else {
			fetch(this.endpoint, {
				method: 'POST',
				body: body,
				headers: {'Content-Type': 'text/plain'},
				keepalive: true,
			}).catch(() => {});
		}
	}

	protected encryptAndSend(json: string): void {
		const iv = crypto.getRandomValues(new Uint8Array(12));
		crypto.subtle.encrypt(
			{name: 'AES-GCM', iv},
			this.encryptKey!,
			new TextEncoder().encode(json)
		).then((ciphertext) => {
			const combined = new Uint8Array(12 + ciphertext.byteLength);
			combined.set(iv, 0);
			combined.set(new Uint8Array(ciphertext), 12);
			let b64 = '';
			for (let i = 0; i < combined.length; i++) {
				b64 += String.fromCharCode(combined[i]);
			}
			this.sendRaw(Analytics.ENCRYPTION_PREFIX + btoa(b64));
		}).catch(() => {
			// Fallback to unencrypted on error
			this.sendRaw(btoa(json));
		});
	}

	protected initEncryptionKey(): void {
		if (!Analytics.ENCRYPTION_KEY || typeof crypto === 'undefined' || !crypto.subtle) {
			return;
		}
		try {
			const keyHex = Analytics.ENCRYPTION_KEY;
			const keyBytes = new Uint8Array(keyHex.length / 2);
			for (let i = 0; i < keyBytes.length; i++) {
				keyBytes[i] = parseInt(keyHex.substring(i * 2, i * 2 + 2), 16);
			}
			crypto.subtle.importKey(
				'raw', keyBytes, {name: 'AES-GCM'}, false, ['encrypt']
			).then((key) => {
				this.encryptKey = key;
			}).catch(() => {});
		}
		catch (e) {}
	}

	protected touchSession(): void {
		sessionStorage.setItem(Analytics.KEY_SID, this.sessionId);
		sessionStorage.setItem(Analytics.KEY_SID_TS, Date.now().toString());
	}

	protected generateUUID(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	protected getUTMParams(): Record<string, string> {
		const params: Record<string, string> = {};
		try {
			const sp = new URL(location.href).searchParams;
			const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
			for (const k of keys) {
				const v = sp.get(k);
				if (v) params[k] = v;
			}
		}
		catch (e) {}
		return params;
	}

	protected getPageLoadTime(): number {
		try {
			const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
			if (nav) {
				return Math.round(nav.loadEventEnd - nav.startTime);
			}
		}
		catch (e) {}
		return 0;
	}
}